import {
    formatNumberByLocale,
    formatPercentageByLocale,
    type LocaleInput,
} from '@/utils/localeFormatters';

import type { BoxTag, ImageBox, ImageSize } from '../types';
import type { BoxTooltipStyle } from '../variants';

/**
 * Where a detection sits on the image, as percentages of the image's own dimensions.
 *
 * Percentages are what let the overlay line up with the <img> without either side knowing
 * the rendered size - the layer only has to match the image's aspect ratio.
 */
export interface BoxGeometry {
    /** Percentages, 0-100, relative to the original image dimensions. */
    left: number;
    top: number;
    width: number;
    height: number;
    tag: BoxTag;
    size_x_mm?: number;
    size_y_mm?: number;
    score: number;
}

/**
 * Converts one bbox from image pixels to overlay percentages.
 *
 * Was `CalculateFishWoundingBox`, which returned `leftProsent`/`topProsent`/`PoiWidth`/
 * `PoiHeight` - half Norwegian, half English, half PascalCase - and nested the tag and
 * sizes under a `meta` key that every caller immediately unwrapped.
 */
export function toBoxGeometry(box: ImageBox, imageSize: ImageSize): BoxGeometry {
    const [x1, y1, x2, y2] = box.bbox;
    // A zero here would make every percentage Infinity or NaN and put the boxes nowhere.
    // ImageStage already refuses to render an image without dimensions; this is the guard
    // for the geometry being reachable from anywhere else.
    const width = imageSize.width || 1;
    const height = imageSize.height || 1;

    return {
        left: (x1 * 100) / width,
        top: (y1 * 100) / height,
        width: ((x2 - x1) * 100) / width,
        height: ((y2 - y1) * 100) / height,
        tag: box.tag,
        size_x_mm: box.size_x_mm,
        size_y_mm: box.size_y_mm,
        score: box.score,
    };
}

interface FormatBoxTooltipArgs {
    box: Pick<BoxGeometry, 'tag' | 'score' | 'size_x_mm' | 'size_y_mm'>;
    style: BoxTooltipStyle;
    isOptoScaleAdmin?: boolean;
    t: (key: string, options: { defaultValue: string }) => string;
    /** The UI language, for the decimal separator in the area and the score. */
    locale: LocaleInput;
}

/**
 * The label shown on hover.
 *
 * Note the early return: a detection with no measured size gets no label at all, including
 * its tag name. That is existing behaviour, and it is why lice boxes without mm sizes are
 * unlabelled even for non-admins. Callers that label unmeasured detections normalise the
 * size themselves rather than being exempted here - see fullSizeLaksvelFinImageMapper -
 * because exempting a style would reach every page that shares it.
 */
export function formatBoxTooltip({
    box,
    style,
    t,
    locale,
    isOptoScaleAdmin = false,
}: FormatBoxTooltipArgs): string {
    const { tag, score, size_x_mm, size_y_mm } = box;

    if (size_x_mm === undefined || size_y_mm === undefined || score === undefined) {
        return '';
    }

    // A tag nobody has translated, such as a bare fin score, shows as it came from the API.
    const tagLabel = t(`organisms.imageViewer.boxTags.${tag}`, { defaultValue: tag });

    if (style === 'woundArea') {
        const areaCm2 = (size_x_mm * size_y_mm) / 100;
        return `${tagLabel} - ${formatNumberByLocale(areaCm2, locale, 2)}cm²`;
    }

    // The confidence score is only shown to OptoScale admins.
    if (style === 'liceScore' && isOptoScaleAdmin) {
        return `${tagLabel} - ${formatPercentageByLocale(score * 100, locale, 0)}`;
    }

    return tagLabel;
}

/**
 * The size the magnified copy of the image is rendered at inside the magnifier.
 *
 * B&W images come off the camera at roughly a third of the colour resolution, and the
 * magnifier's CSS positions the magnified image by offsetting it a fraction of
 * `--salmon-width`/`--salmon-height`. That offset is only correct if the <img> really is
 * that size, so ImageStage (which writes the custom properties) and Magnifier (which sets
 * the inline size) have to agree - they each had their own copy of the factor, with a
 * comment in one asking the reader to keep it in step with the other.
 */
export function magnifiedImageSize(imageSize: ImageSize, isColor: boolean): ImageSize {
    const sizeCompensationFactor = isColor ? 1 : 3;
    return {
        width: imageSize.width * sizeCompensationFactor,
        height: imageSize.height * sizeCompensationFactor,
    };
}
